import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { usePerson } from '../../contexts/PersonContext'
import { canUseDevelopmentVerificationSkip } from '../../lib/verificationStatus'
import { Logo } from './Logo'
import { SidebarNav } from './SidebarNav'
import { FamilySection } from './FamilySection'
import { CirclesSection } from './CirclesSection'
import { GroupsSection } from './GroupsSection'
import { CommunitySection } from './CommunitySection'
import { ViewingAsCard } from './ViewingAsCard'

/** The full left sidebar: brand, primary nav, family, circles, groups, community, viewing-as. */
export function Sidebar({ mobile = false, onProfilePick }: { mobile?: boolean; onProfilePick?: () => void }) {
  return (
    <aside className={`flex flex-none flex-col gap-6 border-r border-line-soft bg-paper-panel px-4 pb-[22px] ${mobile ? 'w-full' : 'w-[252px]'}`}>
      <div className="-mx-4 border-b border-line-soft px-[22px] pb-5 pt-6">
        <Logo insetColor="#f7f1e6" />
      </div>
      <SidebarNav />
      <FamilySection onPick={onProfilePick} />
      <CirclesSection />
      <GroupsSection />
      <CommunitySection />
      <ViewingAsCard />
    </aside>
  )
}

/** Centred warm-paper app card: sidebar + main. Renders route content via Outlet. */
export function AppShell({ children }: { children?: ReactNode }) {
  const [mobile, setMobile] = useState(() => window.matchMedia?.('(max-width: 767px)').matches ?? false)
  const [menuOpen, setMenuOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()
  const { activePerson, verification } = usePerson()

  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 767px)')
    if (!media) return
    const update = () => { setMobile(media.matches); setMenuOpen(false) }
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  useEffect(() => { setMenuOpen(false) }, [pathname, activePerson?.id])

  useEffect(() => {
    if (!menuOpen) return
    const returnFocus = trigger.current
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panel.current?.querySelector<HTMLButtonElement>('button')?.focus()
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') { event.preventDefault(); setMenuOpen(false); return }
      if (event.key !== 'Tab') return
      const targets = panel.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]')
      if (!targets?.length) return
      const first = targets[0], last = targets[targets.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKey)
      returnFocus?.focus()
    }
  }, [menuOpen])

  return (
    <div className="flex min-h-screen justify-center bg-paper-page md:px-6 md:pb-16 md:pt-7">
      <a href="#main-content" onClick={(event) => { event.preventDefault(); document.getElementById('main-content')?.focus() }} className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-btn focus:bg-paper-surface focus:px-4 focus:py-3 focus:text-ink focus:shadow-card">Skip to content</a>
      <div className="flex min-h-screen w-full max-w-[1240px] border-line bg-paper-surface md:min-h-[720px] md:overflow-hidden md:rounded-card md:border md:shadow-card">
        {!mobile && <Sidebar />}
        <div className="min-w-0 flex-1" inert={menuOpen || undefined}>
          {mobile && <header className="flex items-center justify-between gap-3 border-b border-line-soft bg-paper-panel px-5 py-4">
            <span className="font-serif text-xl text-ink">Circle of Trust</span>
            <button ref={trigger} type="button" aria-label="Open navigation" aria-expanded={menuOpen} aria-controls="mobile-navigation" onClick={() => setMenuOpen(true)} className="rounded-btn border border-line px-4 py-2 text-[13px] font-medium text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-terra-deep">Menu</button>
          </header>}
          <main id="main-content" tabIndex={-1} className="min-w-0 bg-paper-surface">
            {canUseDevelopmentVerificationSkip(verification) && <div role="status" className="border-b border-honey-chip bg-honey-bg px-5 py-4 text-[13px] leading-relaxed text-honey-fg">
              <strong>Development test session — payment skipped.</strong>
              <p>Parent verification is incomplete; public launch remains on hold.</p>
            </div>}
            {children ?? <Outlet />}
          </main>
        </div>
      </div>
      {mobile && menuOpen && <div className="fixed inset-0 z-50 bg-ink/35" onClick={(event) => { if (event.target === event.currentTarget) setMenuOpen(false) }}>
        <div ref={panel} id="mobile-navigation" role="dialog" aria-modal="true" aria-label="Navigation" className="h-full w-[min(340px,calc(100%-24px))] overflow-y-auto overscroll-contain bg-paper-panel shadow-card">
          <div className="flex justify-end px-4 pt-4">
            <button type="button" aria-label="Close navigation" onClick={() => setMenuOpen(false)} className="rounded-btn border border-line px-4 py-2 text-[13px] font-medium text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-terra-deep">Close</button>
          </div>
          <Sidebar mobile onProfilePick={() => setMenuOpen(false)} />
        </div>
      </div>}
    </div>
  )
}
